import { InitCommand } from "appworks/commands/comms/init-command";
import { Components } from "appworks/components/components";
import { HeaderComponent } from "appworks/components/header/header-component";
import { PreloaderComponent } from "appworks/components/preloader/preloader-component";
import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { ButtonEvent } from "appworks/graphics/elements/button-element";
import { Layers } from "appworks/graphics/layers/layers";
import { loader } from "appworks/loader/loader";
import { gameState } from "appworks/model/game-state";
import { Model } from "appworks/model/model";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { State } from "appworks/state-machine/states/state";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { UI } from "appworks/ui/ui";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { Timer } from "appworks/utils/timer";

export interface InitConfig {
    // Number of milliseconds before dispatching game inactive sound event
    gameInactiveTime: number;
}
export class Init extends State {

    protected config: InitConfig = {
        gameInactiveTime: 5000
    };

    private inactiveTimer: number;

    constructor(config?: Partial<InitConfig>) {
        super();

        if (config) {
            this.config = { ...this.config, ...config };
        }
    }

    public onEnter() {
        this.setupGameInactiveTimer();

        new Sequence([
            () => InitCommand(),
            () => Components.get(PreloaderComponent).loadComplete(),
            () => this.showOnBoard(),
            () => this.gameLoaded(),
            () => this.startGame()
        ]).then(() => this.complete());
    }

    protected startGame() {
        Services.sound.event(SoundEvent.start_game);

        Layers.jumpToDefaultScene();
        Layers.showAll();
        Components.init();
        UI.start();

        Services.transaction.setBalance(gameState.getCurrentGame().startBalance);

        Components.get(HeaderComponent)?.setMode(Model.read().settings.playMode);

        gameState.getCurrentGame().setToLatestRecord();

        return this.enableSound();
    }

    /**
     * If an onboard is presetn ("Onboard" layer with "onboard" scene):
     * Shows onboard and will wait for continue button click (or screen if no button present)
     * Shows background "onboard" scene or "default" scene if present
     */
    protected showOnBoard() {
        const onboardLayer = Layers.get("Onboard");
        const backgroundLayer = Layers.get("Background");
        const canShow: boolean = Model.read().settings.showIntro;

        if (onboardLayer?.hasScene("onboard") && canShow) {
            if (backgroundLayer && backgroundLayer.hasAnyScene(["onboard", "default"])) {
                backgroundLayer.show();
                backgroundLayer.setScene(backgroundLayer.getFirstValidScene(["onboard", "default"])).execute();
            }

            onboardLayer.show();
            onboardLayer.setScene("onboard").execute();

            return new Contract((resolve) => {

                const hideOnboard = () => {
                    canvasManager.rootNode.removeEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), hideOnboard);
                    continueButton?.off(ButtonEvent.CLICK, hideOnboard);
                    dontShowToggle?.onChanged.removeAll();

                    // Wait for sound unlock before continuing
                    const onSoundUnlocked = () => {
                        if (backgroundLayer) {
                            backgroundLayer.defaultScene().execute();
                        }

                        onboardLayer.defaultScene().then(() => {
                            onboardLayer.hide();
                            resolve();
                        });
                    };

                    if (loader.soundIsUnlocked() || !loader.loadSoundsInPreloader) {
                        onSoundUnlocked();
                    } else {
                        loader.onSoundUnlocked.addOnce(onSoundUnlocked);
                        Timer.setTimeout(() => loader.unlockSound(), 500);
                    }
                };

                const continueButton = onboardLayer.getButton("continue");
                if (continueButton) {
                    continueButton.on(ButtonEvent.CLICK, hideOnboard);
                } else {
                    canvasManager.rootNode.addEventListener(ButtonEvent.POINTER_DOWN.getDOMEventString(), hideOnboard);
                }

                const dontShowToggle = onboardLayer.getToggle("dont_show_toggle");
                if (dontShowToggle) {
                    dontShowToggle.setChecked(Model.read().settings.showIntro);
                    dontShowToggle.onChanged.add((on: boolean) => {
                        const settings = Model.read().settings;
                        settings.showIntro = !on;
                        Model.write({ settings });
                    });
                }
            });
        } else {
            return Contract.empty();
        }
    }

    protected enableSound() {
        if (!Services.sound.getMuted()) {
            Services.sound.unmute();
        }

        return Contract.empty();
    }

    protected setupGameInactiveTimer() {
        uiFlags.onFlagsUpdated.add((flags: number) => {
            if ((flags & UIFlag.IDLE) === UIFlag.IDLE) {
                this.startInactiveTimer();
            } else {
                this.cancelInactiveTimer();
            }
        });
    }

    protected startInactiveTimer() {
        if (!this.inactiveTimer) {
            this.cancelInactiveTimer();
            this.inactiveTimer = Timer.setTimeout(() => Services.sound.customEvent(SoundEvent.game_inactive), this.config.gameInactiveTime);
        }
    }

    protected cancelInactiveTimer() {
        Timer.clearTimeout(this.inactiveTimer);
        this.inactiveTimer = null;
    }

    protected gameLoaded(): Contract<void> {
        loader.unlockSound();

        return Contract.empty();
    }
}
